import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useRecaptcha - loads the Google reCAPTCHA v3 script once per session and
 * exposes an `execute(action)` function that returns a token for a given
 * action name.
 *
 * When `REACT_APP_RECAPTCHA_SITE_KEY` isn't set the hook resolves in
 * "no-op mode" - `execute()` returns an empty string. The backend treats a
 * missing token as skip-validation when its own secret is also unset, so the
 * whole stack degrades gracefully in preview.
 */
const SCRIPT_ID = "recaptcha-v3-script";

export default function useRecaptcha() {
  const siteKey = (process.env.REACT_APP_RECAPTCHA_SITE_KEY || "").trim();
  const [ready, setReady] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!siteKey) return;
    if (window.grecaptcha && window.grecaptcha.ready) {
      window.grecaptcha.ready(() => setReady(true));
      return;
    }
    if (loadedRef.current) return;
    loadedRef.current = true;
    // Inject the script once (survives client-side route changes).
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () =>
        window.grecaptcha?.ready(() => setReady(true))
      );
      return;
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => window.grecaptcha?.ready(() => setReady(true));
    document.head.appendChild(s);
  }, [siteKey]);

  const execute = useCallback(
    async (action = "submit") => {
      if (!siteKey) return "";
      // Wait up to 6 seconds for grecaptcha to load.
      const start = Date.now();
      while (!(window.grecaptcha && window.grecaptcha.execute)) {
        if (Date.now() - start > 6000) {
          console.warn("reCAPTCHA did not load in time");
          return "";
        }
        await new Promise((r) => setTimeout(r, 100));
      }
      try {
        return await window.grecaptcha.execute(siteKey, { action });
      } catch (e) {
        console.warn("reCAPTCHA execute failed:", e);
        return "";
      }
    },
    [siteKey]
  );

  return { ready, execute, enabled: Boolean(siteKey) };
}
